import { GuideRegistryKey } from '@/guide/guideRegistry'
import { storageKey } from '@/constant/storage'
import { GuideStatusRecord } from '@/types/guideStatus'

const readLocalBoolean = (key: string): boolean | undefined => {
  const raw = localStorage.getItem(key)
  if (raw === 'true') return true
  if (raw === 'false') return false
  return undefined
}

export const buildMigratePatchFromLegacyStores = (): GuideStatusRecord => {
  const patch: GuideStatusRecord = {}

  const hasVisitedChat = readLocalBoolean('hasVisitedChat')
  if (hasVisitedChat === true) {
    patch[GuideRegistryKey.LIVE_STUDIO_HAS_VISITED_CHAT] = true
  }

  try {
    const rawGuide = localStorage.getItem('liveStudioGuideShown')
    const parsedGuide = rawGuide ? JSON.parse(rawGuide) : null
    if (parsedGuide?.version) {
      patch[GuideRegistryKey.LIVE_STUDIO_GUIDE_SHOWN] = true
    }
  } catch {
    // ignore invalid JSON
  }

  const lastUsed = Number(localStorage.getItem('lastUsedLiveStudio'))
  if (Number.isFinite(lastUsed) && lastUsed > 0) {
    patch[GuideRegistryKey.LIVE_STUDIO_LAST_USED] = lastUsed
  }

  const liveStudioRedDotSeen = readLocalBoolean(
    'hasSeenLiveStudioRedDot-20251110'
  )
  if (liveStudioRedDotSeen === true) {
    patch[GuideRegistryKey.LIVE_STUDIO_RED_DOT_SEEN_20251110] = true
  }

  const isFirstStory = readLocalBoolean('isFirstEnterStory')
  if (isFirstStory === false) {
    patch[GuideRegistryKey.STORY_FIRST_ENTER_SHOWN] = true
  }

  const hasEnteredStory = readLocalBoolean('hasEnteredStory')
  if (hasEnteredStory === true) {
    patch[GuideRegistryKey.STORY_HAS_ENTERED] = true
  }

  const isFirstChooseModel = readLocalBoolean('isFirstTimeChooseModel')
  if (isFirstChooseModel === false) {
    patch[GuideRegistryKey.MODEL_FIRST_CHOOSE_SHOWN] = true
  }

  const voicePlayConfirmed = readLocalBoolean('voicePlayConfirmed')
  if (voicePlayConfirmed === true) {
    patch[GuideRegistryKey.VOICE_PLAY_CONFIRMED] = true
  }

  const relationshipGuideOpen = readLocalBoolean('relationshipGuideOpen')
  if (relationshipGuideOpen === false) {
    patch[GuideRegistryKey.RELATIONSHIP_GUIDE_SHOWN] = true
  }

  const selfGender = localStorage.getItem(storageKey.SELF_GENDER_SELECTION)
  if (selfGender) {
    patch[GuideRegistryKey.PROFILE_GENDER_SELECTED] = selfGender
  } else {
    const legacyGender = localStorage.getItem('interest_sex')
    if (legacyGender) {
      patch[GuideRegistryKey.PROFILE_GENDER_SELECTED] = legacyGender
    }
  }

  const autoImageModal = readLocalBoolean('has_show_auto_gen_img_modal_v2')
  if (autoImageModal === true) {
    patch[GuideRegistryKey.MEDIA_AUTO_GEN_MODAL_IMAGE_V2] = true
  }

  const autoVideoModal = readLocalBoolean('has_show_auto_gen_video_modal')
  if (autoVideoModal === true) {
    patch[GuideRegistryKey.MEDIA_AUTO_GEN_MODAL_VIDEO] = true
  }

  const autoAudioModal = readLocalBoolean('hasShowAutoGenAudioModal')
  if (autoAudioModal === true) {
    patch[GuideRegistryKey.MEDIA_AUTO_GEN_MODAL_AUDIO] = true
  }

  const firstImageTip = readLocalBoolean('show_first_generate_image_tip')
  if (firstImageTip === true) {
    patch[GuideRegistryKey.MEDIA_FIRST_GEN_TIP_IMAGE] = true
  }

  const firstVideoTip = readLocalBoolean('show_first_generate_video_tip')
  if (firstVideoTip === true) {
    patch[GuideRegistryKey.MEDIA_FIRST_GEN_TIP_VIDEO] = true
  }

  const firstImageConfirm = readLocalBoolean(
    'show_first_generate_image_confirm_modal'
  )
  if (firstImageConfirm === false) {
    patch[GuideRegistryKey.MEDIA_FIRST_GEN_CONFIRM_IMAGE] = true
  }

  const firstVideoConfirm = readLocalBoolean(
    'show_first_generate_video_confirm_modal'
  )
  if (firstVideoConfirm === false) {
    patch[GuideRegistryKey.MEDIA_FIRST_GEN_CONFIRM_VIDEO] = true
  }

  const soundGuideMobileFirst = readLocalBoolean(
    'showVideoSoundGuideOnFirstEntry'
  )
  if (soundGuideMobileFirst === false) {
    patch[GuideRegistryKey.MEDIA_VIDEO_SOUND_GUIDE_MOBILE_FIRST] = true
  }

  const soundGuideMobileAuto = readLocalBoolean(
    'showVideoSoundGuideOnAutoVideo'
  )
  if (soundGuideMobileAuto === false) {
    patch[GuideRegistryKey.MEDIA_VIDEO_SOUND_GUIDE_MOBILE_AUTO] = true
  }

  const soundGuideDesktopFirst = readLocalBoolean(
    'showVideoSoundGuideOnFirstEntryDesktop'
  )
  if (soundGuideDesktopFirst === false) {
    patch[GuideRegistryKey.MEDIA_VIDEO_SOUND_GUIDE_DESKTOP_FIRST] = true
  }

  const soundGuideDesktopAuto = readLocalBoolean(
    'showVideoSoundGuideOnAutoVideoDesktop'
  )
  if (soundGuideDesktopAuto === false) {
    patch[GuideRegistryKey.MEDIA_VIDEO_SOUND_GUIDE_DESKTOP_AUTO] = true
  }

  const newUserModalShown = readLocalBoolean('is_New_User_modal')
  if (newUserModalShown === true) {
    patch[GuideRegistryKey.SUBSCRIPTION_NEW_USER_MODAL_SHOWN] = true
  }

  const firstLevelBannerEnabled = readLocalBoolean('firstLevelUpBannerEnabled')
  if (firstLevelBannerEnabled === false) {
    patch[GuideRegistryKey.LEVEL_FIRST_BANNER_SHOWN] = true
  }

  const rewardTooltipShown = readLocalBoolean('hasShownRewardTooltip')
  if (rewardTooltipShown === true) {
    patch[GuideRegistryKey.LEVEL_REWARD_TOOLTIP_SHOWN] = true
  }

  const chatSettingsRedDot = readLocalBoolean('chatSettingsRedDot')
  if (chatSettingsRedDot === false) {
    patch[GuideRegistryKey.CHAT_SETTINGS_RED_DOT_VIEWED] = true
  }

  const viewedVideoSound = readLocalBoolean('hasViewedVideoSoundSetting')
  if (viewedVideoSound === true) {
    patch[GuideRegistryKey.CHAT_SETTINGS_VIDEO_SOUND_VIEWED] = true
  }

  const viewedFontSize = readLocalBoolean('hasViewedFontSizeSetting')
  if (viewedFontSize === true) {
    patch[GuideRegistryKey.CHAT_SETTINGS_FONT_SIZE_VIEWED] = true
  }

  const summerSaleClosed = readLocalBoolean('hasCloseSummerSaleModal')
  if (summerSaleClosed === true) {
    patch[GuideRegistryKey.MODALS_PROMOTIONS_SUMMER_SALE] = true
  }

  const downloadState = readLocalBoolean(storageKey.SHOW_DOWNLOAD_STATE)
  if (downloadState === false) {
    patch[GuideRegistryKey.MODALS_APP_DOWNLOAD_HOME_BANNER] = true
  }

  return patch
}
